import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import { guardarConConexion } from '../../../nucleo/api/cliente.js';
import { obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import { consultarCuentas, PROPOSITOS } from '../../cuentas/api/cuentasApi.js';
import { editarMovimiento, obtenerMovimiento, registrarTraspaso, TIPOS } from '../api/movimientosApi.js';
import { SaldosAfectados } from '../componentes/CamposDeCaptura.jsx';
import { ComboboxDeCuenta } from '../componentes/ComboboxDeCuenta.jsx';
import { CampoDeFecha } from '../../../nucleo/fecha/CampoDeFecha.jsx';
import { CampoDeImporte } from '../../../nucleo/importes/CampoDeImporte.jsx';
import { useContextoDeRegreso } from '../../../nucleo/navegacion/useContextoDeRegreso.js';
import { conCapturaLimpia, useResumenCedeElPasoAlMenu } from '../navegacion/capturaLimpia.jsx';
/* El formato de los importes de las opciones lo resuelve ahora el combobox, que es
   el único sitio donde se describe una cuenta (H-19, RF-179). */
import AvisoDeError from '../../../nucleo/avisos/AvisoDeError.jsx';

/**
 * Traspaso entre cuentas, PAGO DE TARJETA y DISPOSICIÓN DE EFECTIVO (RF-050,
 * RF-062, D-38, D-46).
 *
 * SON LA MISMA OPERACIÓN: un pago de tarjeta es un traspaso cuyo DESTINO tiene
 * `esCredito`, y una disposición de efectivo, uno cuyo ORIGEN lo tiene. La
 * pantalla cambia de nombre y de textos; el contrato no. Es UN SOLO MOVIMIENTO con
 * dos cuentas, nunca dos espejo (D-33).
 *
 * LOS DOS SELECTORES NO SON IGUALES, y ahí está toda la regla (D-25, RN-067):
 *   · ORIGEN: `CAPTURAR_ORIGEN` — solo cuentas TUYAS y activas.
 *   · DESTINO: `CAPTURAR_DESTINO` — activas DE CUALQUIERA del hogar. Es lo que
 *     permite pagarle la tarjeta a la otra persona o depositarle.
 * El movimiento sigue siendo de quien lo captura (RN-067d, RN-089).
 *
 * LOS DOS CAMPOS DE CUENTA, DESPUÉS DEL INCREMENTO 09 (RF-153, RF-156, D-247,
 * D-255). No son el mismo caso y confundirlos pone la tarjeta en el campo de donde
 * sale el dinero, que es justo el error que RF-153 evita:
 *   · ORIGEN —«de dónde sale»— LLEGA VACÍO SIEMPRE (RN-433b). Antes se sembraba
 *     con `propias.cuentas[0]` cuando la dirección no traía sugerencia: no era «la
 *     última que usaste», era «la que salga primero», y nunca se avisaba (CR-07b).
 *     Lo que SÍ se conserva es la sugerencia `idCuenta` de la dirección, que es
 *     otro parámetro y otro caso.
 *   · DESTINO —«a dónde entra»— se llena con `idCuentaDestino` cuando la dirección
 *     lo trae: es la tarjeta del atajo «Registrar el pago» (RF-156, RN-446a).
 *     ESO SE CONSTRUYÓ EN EL 09: los cuatro enlaces del pago mandaban el parámetro
 *     desde el 6/6 y esta pantalla NUNCA lo leía, así que el botón no cumplía lo
 *     que su propio nombre promete (CR-09, RN-067 sin construir).
 */
/*
 * LAS TRES REDACCIONES DEL AVISO DEL TRASPASO · TEXTO NORMATIVO (RN-436 rev. 6,
 * D-259, D-261, RNF-015.2, D-15). Se escriben TAL CUAL: estas cadenas no se
 * reescriben, no se acortan y no se «mejoran» aquí. La cuarta redacción —la de la
 * captura simple— vive en `CapturaDeMovimiento.jsx` y quedó aprobada sin cambio.
 *
 * CADA AVISO NOMBRA EL CAMPO CON EL RÓTULO QUE EL CAMPO TIENE (D-261). Dicen «a
 * dónde ENTRA» porque la etiqueta del selector de destino dice «A DÓNDE ENTRA»
 * (`:298`) y así lo dibuja el prototipo v26 aprobado. La rev. 5 pidió «a dónde
 * llega» por una transcripción equivocada del rótulo en RN-445; se descartó
 * cambiar la etiqueta —habría obligado a reabrir un diseño ya aprobado—. Si algún
 * día se cambia la etiqueta, estas dos cadenas se cambian CON ella: un aviso que
 * nombra el campo con otras palabras obliga a la persona a traducir para
 * encontrarlo.
 *
 * Lo que NINGUNA de las tres dice, y es decisión expresa:
 *   · NO explican POR QUÉ la cuenta ya no viene puesta: eso sería justificar el
 *     diseño, que RN-442 prohíbe (misma razón de RN-447b).
 *   · NO sustituyen la opción «Elige la cuenta…» del selector: ésa pide la acción
 *     antes de que nadie intente guardar; el aviso aparece SOLO al guardar
 *     (RN-436c).
 */
const AVISOS_DE_CUENTA_FALTANTE = {
  ambas: 'Faltan las cuentas. Elige de dónde sale el dinero y a dónde entra, y vuelve a guardar.',
  origen: 'Falta la cuenta de origen. Elige de dónde sale el dinero y vuelve a guardar.',
  destino: 'Falta la cuenta de destino. Elige a dónde entra el dinero y vuelve a guardar.',
};

/**
 * Cuál de las tres toca, o `null` si no falta ninguna. Fuera del componente y
 * sin estado para que se pueda probar sola (RNF-018: la pantalla no decide
 * reglas, las aplica).
 */
export function avisoDeCuentasFaltantes(idCuenta, idCuentaDestino) {
  if (!idCuenta && !idCuentaDestino) return AVISOS_DE_CUENTA_FALTANTE.ambas;
  if (!idCuenta) return AVISOS_DE_CUENTA_FALTANTE.origen;
  if (!idCuentaDestino) return AVISOS_DE_CUENTA_FALTANTE.destino;
  return null;
}

/**
 * ¿Es utilizable el importe sugerido que llega en la apertura? (PUNTO 2, D-414,
 * RN-726a.) Pura y exportada para que se pueda probar sin montar la pantalla:
 * solo un importe POSITIVO con hasta dos decimales se precarga; cualquier otra
 * cosa —vacío, texto, cero, negativo— se descarta en silencio.
 */
export function esImporteSugeridoUtilizable(valor) {
  if (typeof valor !== 'string') return false;
  const limpio = valor.trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(limpio)) return false;
  return Number(limpio) > 0;
}

/*
 * LO QUE EL FIX DE INGRESOS CAMBIÓ AQUÍ (punto 1): el «+» desde «Traspaso
 * guardado» abre esta pantalla LIMPIA, sin recargar (D-479), con la MISMA pieza
 * compartida que usa la captura simple (`navegacion/capturaLimpia.jsx`). Aquí lo
 * limpio incluye las DOS cuentas y el importe sugerido del atajo del pago: un paso
 * de navegación nuevo monta la pantalla de cero y vuelve a leer la dirección, así
 * que el atajo sigue precargando lo suyo cuando es ÉL el que abre (RN-726a) y no
 * queda nada pegado cuando abre el menú del «+».
 */
function CapturaDeTraspaso() {
  const [parametros] = useSearchParams();
  const { idMovimiento } = useParams();
  const esEdicion = Boolean(idMovimiento);
  const hoy = obtenerFechaDeNegocioHoy();

  /* Regreso al contexto de origen, reemplazando el paso (RF-146, RF-148). */
  const { destinoDeRegreso } = useContextoDeRegreso('/movimientos');

  const [origenes, setOrigenes] = useState([]);
  const [destinos, setDestinos] = useState([]);
  /*
   * RF-187 · UN SOLO INDICADOR PARA LOS DOS SELECTORES, y arranca en VERDADERO en
   * el `useState` inicial, no al empezar la petición: las dos consultas van
   * ENCADENADAS (`consultarCuentas` de origen y luego la de destino), así que entre
   * la una y la otra el destino seguiría sin lista. Con un indicador por campo, el
   * de destino se habilitaría tarde y el de origen temprano, y el salto de
   * maquetación volvería a medias (RN-564). La lista sigue siendo `[]` (RN-566).
   */
  const [cargandoCuentas, setCargandoCuentas] = useState(true);
  const [idCuenta, setIdCuenta] = useState(null);
  const [idCuentaDestino, setIdCuentaDestino] = useState(null);
  const [importe, setImporte] = useState('');
  const [fecha, setFecha] = useState(hoy);
  const [nota, setNota] = useState('');
  const [version, setVersion] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);
  /*
   * RN-436 (rev. 5, D-259). Ya no es un «sí o no»: se guarda CUÁL de las tres
   * redacciones del traspaso toca, porque el aviso único decía «Faltan las
   * cuentas» también cuando faltaba una sola, y eso es un texto de pantalla que
   * afirma algo falso —lo mismo que este incremento corrige en otros cuatro
   * sitios (RF-155, RN-442)—. Se guarda el TEXTO, no las cuentas, para que el
   * aviso diga lo que era verdad AL INTENTAR GUARDAR y no se reescriba solo
   * mientras la persona elige: RN-436(c) manda que aparezca al guardar.
   */
  const [avisoDeCuentaFaltante, setAvisoDeCuentaFaltante] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      setError(null);
      const propias = await consultarCuentas({ proposito: PROPOSITOS.capturarOrigen });
      const delHogar = await consultarCuentas({ proposito: PROPOSITOS.capturarDestino });

      setOrigenes(propias.cuentas);
      setDestinos(delHogar.cuentas);

      if (esEdicion) {
        const { movimiento } = await obtenerMovimiento(idMovimiento);
        setIdCuenta(movimiento.idCuenta);
        setIdCuentaDestino(movimiento.idCuentaDestino);
        setImporte(movimiento.importe);
        setFecha(movimiento.fecha);
        setNota(movimiento.nota ?? '');
        setVersion(movimiento.version);
        return;
      }

      /*
       * ORIGEN. Se retiró `propias.cuentas[0]`: sin sugerencia en la dirección, el
       * campo queda VACÍO (RF-153, RN-433b). `sugerido` se conserva tal cual —es
       * el parámetro `idCuenta`, la cuenta de ORIGEN, y NO es la tarjeta de D-255,
       * que viaja en `idCuentaDestino` y va al otro campo (RN-446b)—.
       */
      const sugerido = Number(parametros.get('idCuenta'));
      if (sugerido && propias.cuentas.some((c) => c.idCuenta === sugerido)) {
        setIdCuenta(sugerido);
      }

      /*
       * DESTINO · SE CONSTRUYE AQUÍ (RF-156, D-255, RN-446a). La tarjeta a pagar
       * la escribe en la dirección el botón «Registrar el pago» de Vencimientos y
       * de Meses sin intereses. Casos borde, resueltos como ya se resuelve el
       * origen: si el parámetro no viene, el destino queda VACÍO; si viene y no es
       * un número o no corresponde a una cuenta ofrecible en el selector de
       * destino, SE DESCARTA EN SILENCIO, sin aviso ni error (mismo criterio que
       * RN-384 y RN-388).
       *
       * EL PARÁMETRO `importe` SIGUE SIN LEERSE, y es decisión, no olvido: el
       * valor inicial del campo de importe está congelado y declarado fuera de
       * alcance (RN-427, D-229, D-241, CR-09c). Lo que SÍ se lee es el dato de
       * la apertura del panel de vencimientos; ver el bloque de abajo.
       */
      const tarjetaDelAtajo = Number(parametros.get('idCuentaDestino'));
      const hayTarjetaDelAtajo = tarjetaDelAtajo
        && delHogar.cuentas.some((c) => c.idCuenta === tarjetaDelAtajo);

      if (hayTarjetaDelAtajo) {
        setIdCuentaDestino(tarjetaDelAtajo);
      }

      /*
       * PUNTO 2 DEL INCREMENTO 19/21 · IMPORTE SUGERIDO AL ABRIR DESDE EL PANEL
       * DE VENCIMIENTOS (D-414, palabras literales de Deisy Paez: «opcion A»;
       * RF-262, RN-726; cierra DEUDA-18-05 y desbloquea CA-18-27 del 18).
       *
       * LA FRONTERA EXACTA DE LO DESCONGELADO, y no un milímetro más:
       *   (a) El enlace «Registrar pago» del panel y del detalle del vencimiento
       *       mandan el pendiente del pago esperado en `importeSugerido`, que es
       *       EL DATO DE LA APERTURA. Esta pantalla lo usa como valor inicial
       *       ÚNICAMENTE cuando llega (RN-726a).
       *   (b) Abierta sin ese dato —alta genérica de traspaso, menú, cualquier
       *       otro camino, incluidos los enlaces de Meses sin intereses, que
       *       mandan `importe` y no `importeSugerido`— arranca VACÍA, idéntica a
       *       hoy (RN-726b, CA-19-21).
       *   (c) El componente de importe del núcleo NO gana ninguna propiedad: la
       *       precarga se resuelve AQUÍ, en la pantalla, con el mismo mecanismo
       *       con que la edición precarga desde el 17 (precedente D-359,
       *       RN-726c).
       *   (d) El valor es EDITABLE, se conserva y queda seleccionado completo al
       *       tomar el foco; el campo jamás se deshabilita (RNF-133, RN-726d).
       *   (e) `autoFocus`, el formato al salir y RN-135 siguen congelados tal
       *       cual (RN-726e).
       *
       * Un valor que no sea un importe positivo SE DESCARTA EN SILENCIO, con el
       * mismo criterio con que se descarta la tarjeta del atajo: nunca un aviso
       * por un parámetro de la dirección.
       */
      const sugeridoDeLaApertura = parametros.get('importeSugerido');
      if (hayTarjetaDelAtajo && esImporteSugeridoUtilizable(sugeridoDeLaApertura)) {
        setImporte(sugeridoDeLaApertura.trim());
      }
    } catch (fallo) {
      setError(fallo);
    } finally {
      /*
       * RN-567 · VUELVE A FALSO ACIERTE O FALLE LA CARGA. Con fallo las dos listas
       * quedan vacías y manda RN-552: los campos no se pintan y el aviso de error
       * de arriba es el que habla. Si la persona reintenta, el indicador vuelve a
       * verdadero y el ciclo se repite.
       */
      setCargandoCuentas(false);
    }
  }, [parametros, esEdicion, idMovimiento]);

  useEffect(() => { cargar(); }, [cargar]);

  /* RN-FIX-M01(b) · ver la nota en `CapturaDeMovimiento.jsx`: con el resumen a la
     vista, el menú del «+» hereda de él el destino de regreso. */
  useResumenCedeElPasoAlMenu(destinoDeRegreso, Boolean(resultado));

  const origen = origenes.find((c) => c.idCuenta === idCuenta);
  const destino = destinos.find((c) => c.idCuenta === idCuentaDestino);

  // LOS TRES NOMBRES EN PANTALLA, decididos por la clase de las dos cuentas
  // (RF-050). No hay un tipo distinto detrás: es el mismo contrato.
  const modo = useMemo(() => {
    if (origen?.esCredito) return MODOS.disposicion;
    if (destino?.esCredito) return MODOS.pago;
    return MODOS.traspaso;
  }, [origen, destino]);

  async function guardar(evento) {
    evento.preventDefault();
    setError(null);

    /* RN-436: sin las dos cuentas elegidas el formulario NO envía, y el aviso usa
       la pieza del núcleo. El origen llega vacío siempre (RN-433b). La
       validación es la MISMA de antes; lo único que cambia es cuál de las tres
       redacciones se muestra (D-259). */
    const avisoQueToca = avisoDeCuentasFaltantes(idCuenta, idCuentaDestino);
    if (avisoQueToca) {
      setAvisoDeCuentaFaltante(avisoQueToca);
      return;
    }
    setAvisoDeCuentaFaltante(null);
    setGuardando(true);

    try {
      const peticion = {
        idCuenta,
        idCuentaDestino,
        importe,
        fecha,
        // Contra ella se decide si la fecha es futura, no contra el reloj del
        // servidor (D-45, RN-075d).
        fechaLocal: hoy,
        nota: nota === '' ? null : nota,
      };

      // Al EDITAR se envía el tipo TRASPASO, que es el que ya tiene: NO se puede
      // cambiar y el servidor lo rechaza si difiere (D-35, RN-073).
      const respuesta = await guardarConConexion(() => (esEdicion
        ? editarMovimiento(idMovimiento, { ...peticion, tipo: TIPOS.traspaso, version })
        : registrarTraspaso(peticion)));

      setResultado(respuesta);
    } catch (fallo) {
      setError(fallo);
    } finally {
      setGuardando(false);
    }
  }

  if (resultado) {
    return (
      <Shell titulo="Traspaso guardado" volverA={destinoDeRegreso}>
        {/* Fila 28 del inventario del incremento 15 (RF-222/RF-223, D-327, RN-628): salió
            el identificador interno, la jerga técnica («transacción») y la justificación de
            la implementación. */}
        <div className="aviso ok" data-prueba="aviso-guardado">
          <b>Traspaso guardado.</b> Las dos cuentas quedaron actualizadas: salió de una y entró a la
          otra.
        </div>

        <SaldosAfectados resultado={resultado} />

        <div className="aviso i">
          Este movimiento aparece en el historial <b>una sola vez</b>, y el filtro por cuenta lo
          encuentra <b>tanto en el origen como en el destino</b>. <b>No es gasto ni
          ingreso</b> y no aparece en ningún total por categoría.
        </div>

        <div style={{ margin: '10px 12px' }}>
          <Link className="btn ancho" to={destinoDeRegreso} replace>Ver el historial</Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell titulo={modo.titulo} subtitulo={modo.subtitulo} volverA={destinoDeRegreso}>
      <AvisoDeError error={error} />

      {avisoDeCuentaFaltante && (
        <AvisoDeError
          mensaje={avisoDeCuentaFaltante}
          datoDePrueba="falta-la-cuenta"
        />
      )}

      {/* CA-FIX-M01 · misma marca estable que la captura simple. */}
      <form onSubmit={guardar} data-prueba="formulario-de-captura">
        <div className="card" style={{ margin: 12 }}>
          <label className="ayuda" htmlFor="importe">IMPORTE</label>
          {/* Regla única de campos de importe (RF-086). Las dos cuentas son de la
              misma moneda, así que la divisa del contexto es la del origen. */}
          <CampoDeImporte
            id="importe"
            name="importe"
            valor={importe}
            alCambiar={setImporte}
            codigoDivisa={origenes.find((c) => c.idCuenta === idCuenta)?.codigoISO ?? null}
            contexto="este traspaso"
            aria-label="Importe"
            style={{ fontSize: 30, fontWeight: 700, width: '100%' }}
          />
          <div className="ayuda">
            Las dos cuentas deben ser de <b>la misma moneda</b>: no hay conversión ni siquiera entre
            personas.
          </div>
        </div>

        <div className="card" style={{ margin: 12 }}>
          {/* H-19 · Los dos selectores del traspaso pasan al MISMO combobox que la
              captura y el filtro del historial (RF-180): se construye una vez y lo
              consumen los cuatro. RN-436 sigue valiendo en los dos: llegan vacíos y
              obligan a elegir, también en el atajo del pago (RN-067, RN-445).
              La lista de cada uno la decide EL SERVIDOR según el propósito de la
              consulta; el combobox no recorta nada por su cuenta (RN-550). */}
          <ComboboxDeCuenta
            id="idCuenta"
            etiqueta="DE DÓNDE SALE — siempre una cuenta TUYA y activa"
            cuentas={origenes}
            valor={idCuenta}
            alElegir={(elegido) => setIdCuenta(elegido ?? null)}
            textoDePeticion="Elige la cuenta de la que sale el dinero…"
            obligatorio
            /* RF-187 · Rótulo y campo presentes desde el primer fotograma, con el
               texto de petición ya aprobado (RN-562, RN-563, CA-11-37). */
            cargando={cargandoCuentas}
            datoDePrueba="combobox-cuenta-origen"
            ayuda={(
              <>
                Las cuentas de la otra persona <b>no aparecen aquí</b>: un traspaso desde su cuenta hacia
                la tuya lo captura ella.
              </>
            )}
          />

          {/* En el destino el «de [propietario]» NO es un adorno: es lo que evita
              mandar dinero a la cuenta equivocada, y por eso es obligatorio en esta
              opción (RF-179). */}
          <ComboboxDeCuenta
            id="idCuentaDestino"
            etiqueta="A DÓNDE ENTRA — puede ser de cualquiera de los dos"
            cuentas={destinos}
            valor={idCuentaDestino}
            alElegir={(elegido) => setIdCuentaDestino(elegido ?? null)}
            textoDePeticion="Elige la cuenta que recibe el dinero…"
            obligatorio
            /* El MISMO indicador que el origen, por lo dicho en su `useState`: las
               dos consultas van encadenadas (RF-187, RN-564). */
            cargando={cargandoCuentas}
            datoDePrueba="combobox-cuenta-destino"
            ayuda={(
              <>
                Aquí <b>sí</b> aparecen las cuentas de la otra persona: es lo que permite pagarle la
                tarjeta o depositarle. El movimiento sigue siendo <b>tuyo</b> y solo tú puedes editarlo o
                cancelarlo.
              </>
            )}
          />

          <div className="campo">
            <label htmlFor="fecha">FECHA</label>
            {/* Campo único de fecha del núcleo (RF-149, RN-400): esta pantalla ya
                no declara `input type="date"` propio (RNF-076). */}
            <CampoDeFecha
              id="fecha"
              name="fecha"
              valor={fecha}
              max={hoy}
              alCambiar={setFecha}
            />
            <span className="ayuda">Sin fecha futura, igual que en el gasto.</span>
          </div>

          <div className="campo">
            <label htmlFor="nota">NOTA (opcional)</label>
            <textarea id="nota" name="nota" maxLength={500} value={nota}
              onChange={(evento) => setNota(evento.target.value)} />
          </div>

          <div className="aviso i">
            <b>Un traspaso no lleva categoría</b> y no es gasto ni ingreso: por eso no aparece en ningún
            total por categoría y no consume presupuesto.
          </div>
        </div>

        <div className="aviso s">{modo.explicacion}</div>

        <div style={{ margin: '10px 12px' }}>
          <button className="btn ancho" type="submit" disabled={guardando}>Guardar traspaso</button>
          <Link className="btn sec ancho" to={destinoDeRegreso} replace>Cancelar</Link>
        </div>
      </form>
    </Shell>
  );
}

/* D-479 · misma envoltura que la captura simple: la corrección se escribe una vez. */
export default conCapturaLimpia(CapturaDeTraspaso);

const MODOS = {
  traspaso: {
    titulo: 'Traspaso entre cuentas',
    subtitulo: 'Un solo movimiento con dos cuentas',
    explicacion: (
      <>
        <b>Traspaso entre dos cuentas.</b> Baja el saldo de una y sube el de la otra. <b>No es gasto ni
        ingreso</b>. Lo único prohibido es <b>tarjeta hacia tarjeta</b> y disponer efectivo
        hacia una cuenta ajena.
      </>
    ),
  },
  pago: {
    titulo: 'Pago de tarjeta',
    subtitulo: 'Es un traspaso, no un gasto',
    explicacion: (
      <>
        <b>Esto es un pago de tarjeta</b>. Baja el saldo de la cuenta que paga y{' '}
        <b>baja la deuda</b> de la tarjeta. <b>No es un gasto</b>: el gasto ya se registró al comprar, y
        volver a contarlo aquí <b>duplicaría el gasto del mes</b>.
      </>
    ),
  },
  disposicion: {
    titulo: 'Disposición de efectivo',
    subtitulo: 'Sacar dinero de tu tarjeta',
    explicacion: (
      <>
        <b>Esto es una disposición de efectivo</b>. Sacaste dinero de tu tarjeta:{' '}
        <b>la deuda SUBE</b> y el saldo de la cuenta que recibe sube también. El destino debe
        ser una cuenta <b>tuya y no de crédito</b>.
        <div className="ayuda">
          <b>Aviso que no bloquea:</b> la <b>comisión por disponer</b> y los <b>intereses</b> que te
          cobre el banco <b>no se calculan aquí</b>. Si los quieres ver, captúralos aparte como un gasto
          con esa misma tarjeta.
        </div>
      </>
    ),
  },
};
